import { normalizeHost } from './url';

const IMOVEIS_STORAGE_VERSION = 'v2';
const LEGACY_EXACT_KEYS = new Set([
  'allDataHome',
  'all_cards',
  'condominios',
  'formatDatas',
  'listCategory1',
  'listEstados',
  'listDatas',
  'listCaracteristicas1',
  'url_ultima_consulta',
]);
const HOME_CACHE_KEY_PREFIXES = ['allDataHome:', 'home_cache:'];

const isLegacyHomeCacheKey = (key: string): boolean => {
  if (LEGACY_EXACT_KEYS.has(key)) return true;
  if (key === 'all_carrosseis') return true;
  if (key.startsWith('all_carrosseis:')) return true;
  if (/^all_carrosseis\d+$/.test(key)) return true;
  if (key.startsWith('imoveis_novidade:')) return true;
  if (key.startsWith('properties_top10:')) return true;
  return false;
};

export const toPositiveStorageInt = (value: unknown): number => {
  const source = Array.isArray(value) ? value[0] : value;
  const parsed = Number(String(source ?? '').replace(/[^\d]/g, ''));
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return parsed;
};

export const getHomeStorageHost = (host: unknown): string => {
  const normalizedHost = normalizeHost(String(host || '')).toLowerCase();
  return normalizedHost || 'unknown-host';
};

export const getHomeStorageScope = (host: unknown, codsite: unknown): string => {
  return `${getHomeStorageHost(host)}:${toPositiveStorageInt(codsite) || 0}`;
};

export const getAllDataHomeStorageKey = (host: unknown, codsite: unknown): string => {
  return `allDataHome:${getHomeStorageScope(host, codsite)}`;
};

export const getHomeScopedStorageKey = (
  baseKey: string,
  host: unknown,
  codsite: unknown,
  ...parts: Array<string | number | null | undefined>
): string => {
  const suffix = parts
    .filter((value) => value !== null && value !== undefined && String(value).length > 0)
    .map((value) => String(value));

  return ['home_cache', baseKey, getHomeStorageHost(host), String(toPositiveStorageInt(codsite) || 0), ...suffix].join(':');
};

export const getImoveisListCategoryStorageKey = (host: unknown, codsite: unknown): string => {
  return getHomeScopedStorageKey(`imoveis_list_category_${IMOVEIS_STORAGE_VERSION}`, host, codsite);
};

export const getImoveisListStatesStorageKey = (host: unknown, codsite: unknown): string => {
  return getHomeScopedStorageKey(`imoveis_list_states_${IMOVEIS_STORAGE_VERSION}`, host, codsite);
};

export const getImoveisListDatasStorageKey = (host: unknown, codsite: unknown): string => {
  return getHomeScopedStorageKey(`imoveis_list_datas_${IMOVEIS_STORAGE_VERSION}`, host, codsite);
};

export const getImoveisListCaracteristicasStorageKey = (host: unknown, codsite: unknown): string => {
  return getHomeScopedStorageKey(`imoveis_list_caracteristicas_${IMOVEIS_STORAGE_VERSION}`, host, codsite);
};

export const getImoveisLastSearchUrlStorageKey = (host: unknown, codsite: unknown): string => {
  return getHomeScopedStorageKey(`imoveis_last_search_url_${IMOVEIS_STORAGE_VERSION}`, host, codsite);
};

export const readJsonStorageValue = <T>(
  storage: Storage | null | undefined,
  key: string,
  fallback: T
): T => {
  if (!storage) return fallback;

  const raw = storage.getItem(key);
  if (raw === null || raw === undefined) return fallback;

  if (typeof raw === 'string' && raw.trim() === '') {
    storage.removeItem(key);
    return fallback;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    storage.removeItem(key);
    return fallback;
  }
};

const isQuotaExceededError = (error: unknown): boolean => {
  if (!(error instanceof DOMException)) return false;
  return (
    error.name === 'QuotaExceededError' ||
    error.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
    error.code === 22 ||
    error.code === 1014
  );
};

const clearOtherHomeCacheKeys = (
  storage: Storage,
  currentKey: string
): void => {
  const keysToRemove: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key || key === currentKey) continue;
    if (!HOME_CACHE_KEY_PREFIXES.some((prefix) => key.startsWith(prefix))) continue;
    keysToRemove.push(key);
  }

  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
};

export const setJsonStorageValue = (
  storage: Storage | null | undefined,
  key: string,
  value: unknown
): boolean => {
  if (!storage) return false;

  let serialized: string;
  try {
    serialized = JSON.stringify(value);
  } catch {
    return false;
  }

  try {
    storage.setItem(key, serialized);
    return true;
  } catch (error) {
    if (!isQuotaExceededError(error)) {
      return false;
    }
  }

  clearLegacyHomeStorageKeys(storage);
  clearOtherHomeCacheKeys(storage, key);

  try {
    storage.setItem(key, serialized);
    return true;
  } catch {
    storage.removeItem(key);
    return false;
  }
};

export const clearLegacyHomeStorageKeys = (storage: Storage | null | undefined): void => {
  if (!storage) return;

  const keysToRemove: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (!key) continue;
    if (!isLegacyHomeCacheKey(key)) continue;
    keysToRemove.push(key);
  }

  for (const key of keysToRemove) {
    storage.removeItem(key);
  }
};
